// @ts-nocheck
export const CALL_TYPE_ORDER = ["INBOUND", "OUTBOUND", "MISSED"];

export function isGroupedPlansFormat(plans) {
  return plans !== null && typeof plans === "object" && !Array.isArray(plans);
}

export function flattenPlansToArray(plans) {
  if (!plans) return [];
  if (Array.isArray(plans)) return plans;

  if (!isGroupedPlansFormat(plans)) return [];

  const result = [];

  for (const type of ["INBOUND", "OUTBOUND"]) {
    const legs = plans[type];
    if (!Array.isArray(legs)) continue;
    legs.forEach((leg) => {
      result.push({
        plan_type: type,
        leg_type: leg.leg_type,
        pulse_duration: leg.pulse_duration,
        pulse_price: leg.pulse_price,
        price: leg.price,
      });
    });
  }

  if (plans.MISSED && typeof plans.MISSED === "object" && !Array.isArray(plans.MISSED)) {
    result.push({
      plan_type: "MISSED",
      pulse_duration: plans.MISSED.pulse_duration ?? 0,
      pulse_price: plans.MISSED.pulse_price,
      price: plans.MISSED.price ?? 0,
    });
  }

  return result;
}

export function groupPlansForStorage(flatPlans) {
  const result = {};

  (flatPlans || []).forEach((plan) => {
    const type = String(plan.plan_type || "").trim().toUpperCase();

    if (type === "MISSED") {
      result.MISSED = {
        pulse_duration: 0,
        pulse_price: Number(plan.pulse_price),
      };
      return;
    }

    if (!result[type]) result[type] = [];
    result[type].push({
      leg_type: String(plan.leg_type).trim().toUpperCase(),
      pulse_duration: Number(plan.pulse_duration),
      pulse_price: Number(plan.pulse_price),
    });
  });

  return result;
}

export function formatVoicePlanId(id) {
  const num = Number(id);
  if (Number.isNaN(num)) return `VP-${id}`;
  return `VP-${String(num).padStart(4, "0")}`;
}

export function formatDurationShort(seconds) {
  if (seconds === 0 || seconds === null || seconds === undefined) return "N/A";
  const n = Number(seconds);
  if (Number.isNaN(n) || n <= 0) return "N/A";
  return n === 1 ? "1s" : `${n}s`;
}

export function formatDurationLabel(seconds) {
  if (seconds === 0 || seconds === null || seconds === undefined) return "N/A";
  const n = Number(seconds);
  if (Number.isNaN(n) || n <= 0) return "N/A";
  return n === 1 ? "1 Second" : `${n} Seconds`;
}

export function formatPriceDisplay(price) {
  const n = Number(price);
  if (Number.isNaN(n)) return String(price ?? "");
  return n.toFixed(2);
}

export function getPlansGroupedByCallType(plans) {
  if (isGroupedPlansFormat(plans)) {
    return CALL_TYPE_ORDER.filter((type) => {
      if (type === "MISSED") {
        return plans.MISSED && typeof plans.MISSED === "object";
      }
      return Array.isArray(plans[type]) && plans[type].length > 0;
    }).map((type) => ({
      type,
      legs:
        type === "MISSED"
          ? [{ plan_type: "MISSED", ...plans.MISSED }]
          : plans[type].map((leg) => ({ plan_type: type, ...leg })),
    }));
  }

  const buckets = { INBOUND: [], OUTBOUND: [], MISSED: [] };
  (plans || []).forEach((plan) => {
    const type = String(plan?.plan_type || "").toUpperCase();
    if (buckets[type]) buckets[type].push(plan);
  });
  return CALL_TYPE_ORDER.filter((type) => buckets[type].length > 0).map(
    (type) => ({ type, legs: buckets[type] })
  );
}

export function getUniqueCallTypes(plans) {
  if (isGroupedPlansFormat(plans)) {
    return CALL_TYPE_ORDER.filter((type) => {
      if (type === "MISSED") {
        return plans.MISSED && typeof plans.MISSED === "object";
      }
      return Array.isArray(plans[type]) && plans[type].length > 0;
    });
  }

  return CALL_TYPE_ORDER.filter((type) =>
    (plans || []).some(
      (plan) => String(plan?.plan_type || "").toUpperCase() === type
    )
  );
}

export function getCallTypeSummaryLine(group, field) {
  const { type, legs } = group;
  if (type === "MISSED") {
    return field === "duration"
      ? "N/A"
      : formatPriceDisplay(legs[0]?.pulse_price);
  }
  if (legs.length === 1) {
    return field === "duration"
      ? formatDurationShort(legs[0]?.pulse_duration)
      : formatPriceDisplay(legs[0]?.pulse_price);
  }
  return legs
    .map((leg) => {
      const legLabel = getLegTypeLabel(leg.leg_type);
      const value =
        field === "duration"
          ? formatDurationShort(leg.pulse_duration)
          : formatPriceDisplay(leg.pulse_price);
      return `${legLabel}: ${value}`;
    })
    .join(", ");
}

export const SHARED_LEG_TYPE = [
  { label: "Customer PSTN", value: "CUSTOMER_PSTN" },
  { label: "Agent PSTN", value: "AGENT_PSTN" },
  { label: "Agent WebRTC", value: "AGENT_WEBRTC" },
];

export const INBOUND_ONLY_LEG_TYPE = [
  { label: "AI Agent", value: "AI_AGENT" },
];

export const LEG_TYPE = [...SHARED_LEG_TYPE, ...INBOUND_ONLY_LEG_TYPE];

const INBOUND_LEG_KEYS = LEG_TYPE.map((l) => `INBOUND_${l.value}`);
const OUTBOUND_LEG_KEYS = SHARED_LEG_TYPE.map((l) => `OUTBOUND_${l.value}`);

export function getLegTypesForPlanType(planType) {
  const type = String(planType || "").trim().toUpperCase();
  if (type === "INBOUND") return LEG_TYPE;
  if (type === "OUTBOUND") return SHARED_LEG_TYPE;
  return [];
}

export function getVoicePlanKey(plan) {
  const type = String(plan?.plan_type || "").trim().toUpperCase();
  const leg = plan?.leg_type ? String(plan.leg_type).trim().toUpperCase() : null;
  return leg ? `${type}_${leg}` : type;
}

export function getVoicePlanLabel(plan) {
  if (!plan?.leg_type) return plan?.plan_type || "";
  const leg = LEG_TYPE.find((l) => l.value === plan.leg_type);
  return `${plan.plan_type} - ${leg?.label || plan.leg_type}`;
}

export function getLegTypeLabel(legType) {
  if (!legType) return "N/A";
  const leg = LEG_TYPE.find((l) => l.value === legType);
  return leg?.label || legType;
}

export function isInboundComplete(currentPlan) {
  return INBOUND_LEG_KEYS.every((k) => currentPlan.includes(k));
}

export function isOutboundComplete(currentPlan) {
  return OUTBOUND_LEG_KEYS.every((k) => currentPlan.includes(k));
}

export function isCallTypeAvailable(callType, currentPlan) {
  if (callType === "MISSED") return !currentPlan.includes("MISSED");
  if (callType === "INBOUND") return !isInboundComplete(currentPlan);
  if (callType === "OUTBOUND") return !isOutboundComplete(currentPlan);
  return !currentPlan.includes(callType);
}

export function isPlanRowComplete(plan) {
  const price = plan?.pulse_price;
  if (price === "" || price === null || price === undefined) return false;

  const priceNum = Number(price);
  if (Number.isNaN(priceNum) || priceNum < 0) return false;

  if (plan.plan_type !== "MISSED") {
    const duration = plan?.pulse_duration;
    if (duration === "" || duration === null || duration === undefined) return false;
    if (Number(duration) <= 0) return false;
  }

  return true;
}

export function hasMissedPlanWithPulsePrice(plans) {
  return flattenPlansToArray(plans).some(
    (p) =>
      String(p?.plan_type).toUpperCase() === "MISSED" &&
      Number(p?.pulse_price) > 0
  );
}

export function areAllPlansComplete(plans) {
  if (!Array.isArray(plans) || plans.length === 0) return false;
  return plans.every(isPlanRowComplete);
}

export function getSubmittablePlans(plans, lockedRowKeys = null) {
  const complete = (plans || []).filter(isPlanRowComplete);
  if (lockedRowKeys === null || lockedRowKeys === undefined) {
    return complete;
  }
  return complete.filter((plan) =>
    lockedRowKeys.includes(getVoicePlanKey(plan))
  );
}

export const DEFAULT_VOICE_PLAN_PULSE_DURATION = 1;
export const DEFAULT_VOICE_PLAN_PULSE_PRICE = "0.00";

export function buildVoicePlanLegEntry(planType, legType) {
  const type = String(planType || "").trim().toUpperCase();
  const isMissed = type === "MISSED";

  return {
    pulse_price: DEFAULT_VOICE_PLAN_PULSE_PRICE,
    pulse_duration: isMissed ? 0 : DEFAULT_VOICE_PLAN_PULSE_DURATION,
    plan_type: type,
    ...(!isMissed && legType ? { leg_type: legType } : {}),
  };
}

export function buildAllVoicePlanLegEntries(allowedPlanTypes) {
  const types = Array.isArray(allowedPlanTypes)
    ? CALL_TYPE_ORDER.filter((type) => allowedPlanTypes.includes(type)).map(
        (type) => ({ value: type, label: type })
      )
    : CALL_TYPE_ORDER.map((type) => ({ value: type, label: type }));

  const entries = [];

  types.forEach((planType) => {
    if (planType.value === "MISSED") {
      entries.push(buildVoicePlanLegEntry("MISSED"));
      return;
    }

    getLegTypesForPlanType(planType.value).forEach((leg) => {
      entries.push(buildVoicePlanLegEntry(planType.value, leg.value));
    });
  });

  return entries;
}

export function getAllVoicePlanLegKeys(allowedPlanTypes) {
  return buildAllVoicePlanLegEntries(allowedPlanTypes).map((plan) =>
    getVoicePlanKey(plan)
  );
}
